import { FC, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import FloatingInput from '@/components/ui/FloatingInput'
import { useAuth } from '@/hooks'
import { useI18n } from '@/hooks'
import { i18n } from '@/lang'
import type { UpdateProfileRequest } from '@/types/auth'

const authProviderLabel = (provider: string, t: (typeof i18n)['en']) => {
  if (provider === 'google') return t.authProviderGoogle
  if (provider === 'email') return t.authProviderEmail
  if (provider === 'both') return t.authProviderBoth
  return provider
}

const ProfilePage: FC = () => {
  const navigate = useNavigate()
  const { lang } = useI18n()
  const { user, isAuthenticated, isLoading, updateProfile, logout } = useAuth()
  const t = i18n[lang]

  const [displayName, setDisplayName] = useState('')
  const [phone, setPhone] = useState('')
  const [saving, setSaving] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/')
    }
  }, [isLoading, isAuthenticated, navigate])

  useEffect(() => {
    if (user) {
      setDisplayName(user.name || '')
      setPhone(user.phone || '')
    }
  }, [user])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!displayName.trim()) return
    setSaving(true)
    setSuccessMsg('')
    setErrorMsg('')
    try {
      const req: UpdateProfileRequest = {
        displayName: displayName.trim(),
        phoneNumber: phone.trim(),
      }
      await updateProfile(req)
      setSuccessMsg(t.profileUpdated)
    } catch {
      setErrorMsg(lang === 'zh' ? '更新失敗，請稍後再試。' : 'Failed to update. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  if (isLoading || !user) {
    return (
      <main className="pt-14 min-h-screen flex items-center justify-center text-gray-400 text-sm">
        {lang === 'zh' ? '載入中...' : 'Loading...'}
      </main>
    )
  }

  return (
    <main className="pt-14 min-h-screen bg-gray-50">
      <div className="max-w-lg mx-auto px-4 py-12">
        {/* Avatar + info */}
        <div className="flex flex-col items-center gap-3 mb-10">
          {user.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-20 h-20 rounded-full object-cover border border-gray-200 shadow-sm"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-2xl text-gray-500">
              {user.name?.[0]?.toUpperCase() ?? '?'}
            </div>
          )}
          <div className="text-center">
            <p className="font-medium text-gray-900">{user.name}</p>
            <p className="text-sm text-gray-500">{user.email}</p>
            <p className="text-xs text-gray-400 mt-1">
              {t.authProvider}: {authProviderLabel(user.authProvider ?? 'google', t)}
            </p>
          </div>
        </div>

        <h1 className="text-2xl font-medium mb-6">{t.editProfile}</h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div>
            <FloatingInput label={t.displayName} type="text" value={displayName} onChange={setDisplayName} />
          </div>
          <div>
            <FloatingInput label={t.phoneNumber} type="tel" value={phone} onChange={setPhone} />
          </div>

          {successMsg && (
            <div className="p-3 bg-green-50 border border-green-200 text-green-700 text-sm">{successMsg}</div>
          )}
          {errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm">{errorMsg}</div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="h-12 bg-black text-white text-sm font-medium tracking-wide hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? '...' : t.saveChanges}
          </button>
        </form>

        <div className="mt-8 border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full h-11 border border-gray-300 text-sm font-medium text-gray-600 hover:border-gray-800 hover:text-black transition-colors"
          >
            {t.logout}
          </button>
        </div>
      </div>
    </main>
  )
}

export default ProfilePage
